import { addDays } from 'date-fns';
import { ChevronRight, FileCheck, Pencil, Trash, Upload } from 'lucide-react';
import DateCalendar from '../../../helpers/UI/DateCalendar/DateCalendar';
import { RentReportingFormik } from '../types';

type UploadLeaseStepProps = {
  formik: RentReportingFormik;
  fileInputRef: React.MutableRefObject<HTMLInputElement | null>;
  selectedFile: string;
  fileName?: string;
  isUploading: boolean;
  uploadProgress: number;
  uploadError: string | null;
  openDocument: () => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  onClearFile: () => void;
  startDate: Date | null;
  endDate: Date | null;
  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;
  isManualAddress: boolean;
  setIsManualAddress: (value: boolean) => void;
  addressInputRef: React.MutableRefObject<HTMLInputElement | null>;
  canadianProvinces: { code: string; name: string }[];
};

const UploadLeaseStep = ({
  formik,
  fileInputRef,
  selectedFile,
  fileName,
  isUploading,
  uploadProgress,
  uploadError,
  openDocument,
  onFileChange,
  onClearFile,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  isManualAddress,
  setIsManualAddress,
  addressInputRef,
  canadianProvinces,
}: UploadLeaseStepProps) => (
  <div className='flex flex-col items-start justify-center h-full gap-8 px-10'>
    <div className='space-y-4'>
      <span className='text-main font-bold block text-[18px]'>Upload your lease</span>
      <span className='text-main block text-base'>
        We know this step can be a bit of a pain, but Equifax requires TenantPay to validate your
        lease before we can start reporting. We can&apos;t begin until we have a valid lease
        agreement, including the lease start and end dates.
      </span>
    </div>

    <div className='flex flex-col items-start gap-6 w-full'>
      <div className={'inputWrapper w-full'}>
        {!selectedFile ? (
          <>
            <div className={'label'}>
              Upload file<span>*</span>
            </div>
            <div
              className={'inputField flex gap-3 cursor-pointer'}
              onClick={() => {
                if (!isUploading) {
                  fileInputRef.current?.click();
                }
              }}
            >
              <input
                onChange={onFileChange}
                multiple={false}
                ref={(node) => {
                  fileInputRef.current = node;
                }}
                type={selectedFile === '' ? 'file' : ''}
                hidden
                accept='.pdf'
              />
              <div className='flex items-center gap-2'>
                <Upload className='text-gray-500 h-5 w-5' />
                <span className='text-light/70'>Choose a file</span>
              </div>
            </div>
            {isUploading && (
              <div role='status' aria-live='polite' className='mt-2'>
                <div className='text-sm text-main'>Uploading your lease… {uploadProgress}%</div>
                <div className='mt-1 h-1.5 w-full rounded-full bg-[#E8EAF0]'>
                  <div
                    className='h-1.5 rounded-full bg-primary transition-all'
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
            {uploadError && !isUploading && (
              <div role='alert' className='error mt-2'>
                {uploadError}
              </div>
            )}
            <span className='text-[#8A92AB] text-xs'>We support PDF documents only</span>
          </>
        ) : (
          <>
            <div className={'inputField flex gap-3 cursor-pointer'}>
              <div className='flex items-center gap-2'>
                <FileCheck className='text-gray-500 h-5 w-5' />
                <span
                  className='text-light/70'
                  onClick={(e) => {
                    e.preventDefault();
                    openDocument();
                  }}
                >
                  {fileName}
                </span>
                {selectedFile && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      onClearFile();
                    }}
                  >
                    <Trash className='text-gray-500 h-5 w-5' />
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <div className='flex w-full gap-5'>
        <div className='inputWrapper w-full'>
          <div className={'label'}>
            Lease start date<span>*</span>
          </div>
          <div className={'inputField '}>
            <DateCalendar
              startDate={startDate}
              maxDate={endDate ? addDays(endDate, -1) : undefined}
              onDateChange={setStartDate}
              formik={formik}
              type='leaseStart'
              placement='topEnd'
            />
          </div>
        </div>
        <div className='inputWrapper w-full'>
          <div className={'label'}>
            Lease end date<span>*</span>
          </div>
          <div className={'inputField '}>
            <DateCalendar
              startDate={endDate}
              minDate={startDate ? addDays(startDate, 1) : undefined}
              onDateChange={setEndDate}
              formik={formik}
              type='leaseEnd'
              placement='topEnd'
            />
          </div>
        </div>
      </div>
      <>
        <span className='text-main block text-base mt-10'>
          All the address fields are required to proceed with rent reporting.
        </span>
        <div className='flex w-full flex-col mt-4'>
          <div className='flex items-center justify-between gap-2 mb-2'>
            <div className={'label-light'}>
              Address<span>*</span>
            </div>
            {!isManualAddress && (
              <button
                type='button'
                className='flex items-center gap-1.5 text-sm text-primary hover:underline'
                onClick={() => setIsManualAddress(true)}
              >
                <Pencil className='h-4 w-4' />
                <span>Enter manually</span>
              </button>
            )}
          </div>
          {!isManualAddress ? (
            <>
              <div className={'inputWrapper w-full'}>
                <div className={'inputField '}>
                  <input
                    ref={(node) => {
                      addressInputRef.current = node;
                    }}
                    type='text'
                    autoComplete='off'
                    {...formik.getFieldProps('address')}
                    className='text-main'
                    placeholder='Enter your full address'
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  />
                </div>
                {formik.touched.address && formik.errors.address && (
                  <div className='error'>{formik.errors.address}</div>
                )}
              </div>
              <div className={'inputWrapper w-full mt-4'}>
                <div className={'label-light'}>
                  Unit number<span>*</span>
                </div>
                <div className={'inputField '}>
                  <input
                    type='text'
                    autoComplete='off'
                    {...formik.getFieldProps('unitNumber')}
                    className='text-main'
                    placeholder='Enter unit number'
                    onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                  />
                </div>
                {formik.touched.unitNumber && formik.errors.unitNumber && (
                  <div className='error'>{formik.errors.unitNumber}</div>
                )}
              </div>
            </>
          ) : (
            <div className='flex flex-col gap-6 mt-4'>
              <button
                type='button'
                className='self-start flex items-center gap-1 text-sm text-light/70 hover:text-main mb-2'
                onClick={() => setIsManualAddress(false)}
              >
                <ChevronRight className='h-4 w-4 rotate-180' />
                <span>Back to address search</span>
              </button>
              <div className='flex gap-5 w-full flex-wrap'>
                <div className={'inputWrapper flex-1 min-w-[140px]'}>
                  <div className={'label-light'}>
                    Building/street number<span>*</span>
                  </div>
                  <div className={'no-border-input-field'}>
                    <input
                      type='text'
                      autoComplete='off'
                      {...formik.getFieldProps('house')}
                      placeholder='Enter building/street number'
                      className='text-main'
                      onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                    />
                  </div>
                  {formik.touched.house && formik.errors.house && (
                    <div className='error'>{formik.errors.house}</div>
                  )}
                </div>
                <div className={'inputWrapper flex-1 min-w-[140px]'}>
                  <div className={'label-light'}>
                    Unit number<span>*</span>
                  </div>
                  <div className={'no-border-input-field'}>
                    <input
                      type='text'
                      autoComplete='off'
                      {...formik.getFieldProps('unitNumber')}
                      placeholder='Enter unit number'
                      className='text-main'
                      onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                    />
                  </div>
                  {formik.touched.unitNumber && formik.errors.unitNumber && (
                    <div className='error'>{formik.errors.unitNumber}</div>
                  )}
                </div>
              </div>
              <div className='flex gap-5 w-full flex-wrap'>
                <div className={'inputWrapper flex-1 min-w-[140px]'}>
                  <div className={'label-light'}>
                    Street name<span>*</span>
                  </div>
                  <div className={'no-border-input-field'}>
                    <input
                      type='text'
                      autoComplete='off'
                      {...formik.getFieldProps('street')}
                      placeholder='Enter your street name'
                      className='text-main'
                      onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                    />
                  </div>
                  {formik.touched.street && formik.errors.street && (
                    <div className='error'>{formik.errors.street}</div>
                  )}
                </div>
                <div className={'inputWrapper flex-1 min-w-[140px]'}>
                  <div className={'label-light'}>
                    City<span>*</span>
                  </div>
                  <div className={'no-border-input-field'}>
                    <input
                      type='text'
                      autoComplete='off'
                      {...formik.getFieldProps('city')}
                      placeholder='Enter your city'
                      className='text-main'
                      onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                    />
                  </div>
                  {formik.touched.city && formik.errors.city && (
                    <div className='error'>{formik.errors.city}</div>
                  )}
                </div>
              </div>
              <div className='flex gap-5 w-full flex-wrap'>
                <div className={'inputWrapper flex-1 min-w-[140px]'}>
                  <div className={'label-light'}>
                    Province<span>*</span>
                  </div>
                  <div className={'no-border-input-field'}>
                    <input
                      type='text'
                      autoComplete='off'
                      {...formik.getFieldProps('province')}
                      placeholder='Enter your province'
                      className='text-main'
                      onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                    />
                  </div>
                  {formik.touched.province && formik.errors.province && (
                    <div className='error'>{formik.errors.province}</div>
                  )}
                </div>
                <div className={'inputWrapper flex-1 min-w-[140px]'}>
                  <div className={'label-light'}>
                    Province code<span>*</span>
                  </div>
                  <div className={'no-border-input-field'}>
                    <select
                      id='provinceCode'
                      className='text-main w-full bg-transparent border-none outline-none'
                      value={formik.values.provinceCode}
                      onChange={(e) => {
                        const option = canadianProvinces.find((p) => p.code === e.target.value);
                        if (option) {
                          formik.setFieldValue('province', option.name);
                          formik.setFieldValue('provinceCode', option.code);
                        }
                      }}
                      onBlur={() => formik.setFieldTouched('provinceCode', true)}
                    >
                      <option value=''>Select province</option>
                      {canadianProvinces.map((p) => (
                        <option key={p.code} value={p.code}>
                          {p.code} - {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {formik.touched.provinceCode && formik.errors.provinceCode && (
                    <div className='error'>{formik.errors.provinceCode}</div>
                  )}
                </div>
              </div>
              <div className='flex gap-5 w-full flex-wrap'>
                <div className={'inputWrapper flex-1 min-w-[140px]'}>
                  <div className={'label-light'}>
                    Postal code<span>*</span>
                  </div>
                  <div className={'no-border-input-field'}>
                    <input
                      type='text'
                      autoComplete='off'
                      {...formik.getFieldProps('postal')}
                      placeholder='Enter postal code'
                      className='text-main'
                      onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                    />
                  </div>
                  {formik.touched.postal && formik.errors.postal && (
                    <div className='error'>{formik.errors.postal}</div>
                  )}
                </div>
                <div className={'inputWrapper flex-1 min-w-[140px]'}>
                  <div className={'label-light'}>Country</div>
                  <div className={'no-border-input-field'}>
                    <span className='text-main'>Canada</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    </div>
  </div>
);

export default UploadLeaseStep;
